import React from 'react';
import type { MarketProfileState } from '../marketProfileStore';
import { isQuestionnaireSubmitted } from '../marketProfileStore';
import { ProgressBar, ReviewCountdown } from '../MarketProfileShared';

type Props = {
  state: MarketProfileState;
  completed: number;
  submitted: number;
  onGoFill: () => void;
  onOpenPosition?: () => void;
  onOpenGrading?: () => void;
};

export default function MarketDataLockedState({
  state,
  completed,
  submitted,
  onGoFill,
  onOpenPosition,
  onOpenGrading,
}: Props) {
  const positionSubmitted = isQuestionnaireSubmitted(state.position.status);
  const gradingSubmitted = isQuestionnaireSubmitted(state.grading.status);
  const positionPending = state.position.status === 'pending_review';
  const gradingPending = state.grading.status === 'pending_review';
  const positionDone = state.position.status === 'complete';
  const gradingDone = state.grading.status === 'complete';
  const anyPending = positionPending || gradingPending;

  let title = 'Рыночные данные пока недоступны';
  let text = 'Заполните данные о должности и пройдите грейдинг, чтобы открыть сравнение компенсации и уровня роли с рынком.';

  if (anyPending && submitted > completed) {
    title = 'Данные на проверке';
    text = 'Отправленные анкеты проверяются до 24 часов. После проверки обеих анкет вы сможете подтвердить данные и открыть рыночные ориентиры.';
  } else if (completed === 1 && positionDone) {
    title = 'Осталось пройти грейдинг роли';
    text = 'Данные о должности проверены. Пройдите грейдинг, чтобы увидеть положение в структуре ролей и рыночные ориентиры.';
  } else if (completed === 1 && gradingDone) {
    title = 'Осталось заполнить данные о должности';
    text = 'Грейдинг проверен. Заполните данные о должности и компенсации, чтобы открыть сравнение с рынком.';
  } else if (submitted === 1 && positionPending) {
    title = 'Данные о должности на проверке';
    text = 'Анкета отправлена. Пока идёт проверка, вы можете пройти грейдинг роли.';
  } else if (submitted === 1 && gradingPending) {
    title = 'Грейдинг на проверке';
    text = 'Ответы отправлены. Пока идёт проверка, вы можете заполнить данные о должности и компенсации.';
  } else if (submitted === 1 && positionSubmitted) {
    title = 'Осталось пройти грейдинг роли';
    text = 'Данные о должности заполнены. Пройдите грейдинг, чтобы увидеть положение в структуре ролей и рыночные ориентиры.';
  } else if (submitted === 1) {
    title = 'Осталось заполнить данные о должности';
    text = 'Грейдинг пройден. Заполните данные о должности и компенсации, чтобы открыть сравнение с рынком.';
  }

  const primaryCta = ((): { label: string; action: () => void } | null => {
    if (submitted >= 2 && completed < 2) return null;
    if (anyPending && positionPending && !gradingSubmitted && onOpenGrading) {
      return { label: 'Пройти грейдинг', action: onOpenGrading };
    }
    if (anyPending && gradingPending && !positionSubmitted && onOpenPosition) {
      return { label: 'Заполнить данные', action: onOpenPosition };
    }
    if (submitted === 1 && positionSubmitted && !gradingSubmitted && onOpenGrading) {
      return { label: 'Пройти грейдинг', action: onOpenGrading };
    }
    if (submitted === 1 && !positionSubmitted && onOpenPosition) {
      return { label: 'Заполнить данные', action: onOpenPosition };
    }
    if (submitted === 0) {
      return { label: 'Перейти к заполнению данных', action: onGoFill };
    }
    return null;
  })();

  const progressPct = submitted > 0 ? (submitted / 2) * 100 : (completed / 2) * 100;
  const progressLabel =
    anyPending
      ? `${submitted} из 2 анкет отправлено · ${completed} проверено`
      : submitted === 1
        ? '1 из 2 анкет заполнено'
        : `${completed} из 2 анкет заполнено`;

  return (
    <div className="dcf-mp-md-locked">
      <section className="dcf-mp-card dcf-mp-md-summary">
        <div className="dcf-mp-md-summary__main">
          <h1 className="dcf-mp-hero__title">Рыночные данные</h1>
          <p className="dcf-mp-hero__desc">
            Сравните компенсацию и уровень роли с релевантной рыночной группой.
          </p>
        </div>
        <span className="dcf-mp-access-badge">
          <i className="fas fa-lock" aria-hidden="true" />
          Доступ не открыт
        </span>
      </section>

      <section className="dcf-mp-card">
        <h2 className="dcf-mp-card__title">{title}</h2>
        <p className="dcf-mp-action-card__desc">{text}</p>

        {(submitted > 0 || completed > 0) && (
          <div className="dcf-mp-md-progress-wrap">
            <div className="dcf-mp-card__head">
              <span className="dcf-mp-progress-count">{progressLabel}</span>
            </div>
            <ProgressBar value={progressPct} />
          </div>
        )}

        {positionPending && state.position.reviewSubmittedAt && (
          <div className="dcf-mp-md-pending-item">
            <span className="dcf-status-pill dcf-mp-pill--review">
              <i className="fas fa-hourglass-half" aria-hidden="true" />
              Должность на проверке
            </span>
            <ReviewCountdown reviewSubmittedAt={state.position.reviewSubmittedAt} />
          </div>
        )}

        {gradingPending && state.grading.reviewSubmittedAt && (
          <div className="dcf-mp-md-pending-item">
            <span className="dcf-status-pill dcf-mp-pill--review">
              <i className="fas fa-hourglass-half" aria-hidden="true" />
              Грейдинг на проверке
            </span>
            <ReviewCountdown reviewSubmittedAt={state.grading.reviewSubmittedAt} />
          </div>
        )}

        <div className="dcf-mp-md-checklist">
          <div className={`dcf-mp-md-check ${
            positionDone ? 'dcf-mp-md-check--done' : positionPending ? 'dcf-mp-md-check--pending' : ''
          }`}>
            <span className="dcf-mp-step__check">
              {positionDone && <i className="fas fa-check" aria-hidden="true" />}
              {positionPending && <i className="fas fa-hourglass-half" aria-hidden="true" />}
            </span>
            <span>Данные о должности и компенсации</span>
          </div>
          <div className={`dcf-mp-md-check ${
            gradingDone ? 'dcf-mp-md-check--done' : gradingPending ? 'dcf-mp-md-check--pending' : ''
          }`}>
            <span className="dcf-mp-step__check">
              {gradingDone && <i className="fas fa-check" aria-hidden="true" />}
              {gradingPending && <i className="fas fa-hourglass-half" aria-hidden="true" />}
            </span>
            <span>Грейдинг роли</span>
          </div>
        </div>

        {primaryCta && (
          <button type="button" className="dcf-btn dcf-btn--primary" onClick={primaryCta.action}>
            {primaryCta.label}
          </button>
        )}
      </section>

      <section className="dcf-mp-card">
        <div className="dcf-mp-card__head">
          <h2 className="dcf-mp-card__title">Что откроется</h2>
          <span className="dcf-mp-lock-note">
            <i className="fas fa-lock" aria-hidden="true" />
            Предпросмотр
          </span>
        </div>
        <div className="dcf-mp-preview-grid dcf-mp-md-preview-grid--locked">
          <div className="dcf-mp-preview-card">
            <div className="dcf-mp-preview-card__title">Компенсация относительно рынка</div>
            <div className="dcf-mp-preview-card__masked">
              <span className="dcf-mp-masked">ниже / в рынке / выше</span>
              <div className="dcf-mp-preview-bar" />
            </div>
          </div>
          <div className="dcf-mp-preview-card">
            <div className="dcf-mp-preview-card__title">Шкала P25 — P90</div>
            <div className="dcf-mp-preview-card__masked">
              <span className="dcf-mp-masked">медиана · перцентиль</span>
              <div className="dcf-mp-preview-bar" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
